/* eslint-env browser */
import PropTypes from 'prop-types';
import React from 'react';

import MoreVert from '@mui/icons-material/MoreVert';
import { Menu, ListItem, Separator, Tooltip } from '@ublend-npm/aulaui-next';

import readUploadedFileContent from '../../../../../utils/readUploadedFileContent';
import {
  MATERIAL_CREATE_FOLDER,
  IMPORT_MATERIAL,
  EXPORT_MATERIAL,
  MATERIAL_IMPORT_EXPORT_TOOLTIP,
  RESTORE_PAGE,
} from '../../../../../constants/texts';
import { evaluateMaterialAction } from '@core/materials/permissions';

import { uppercaseFirst } from './common';
import { UploadButton, moreMenu, menuItem } from './MaterialMenu.styled';
import { IconButton } from '@ublend-npm/aulaui-next';

class MaterialMenu extends React.PureComponent {
  constructor(props) {
    super(props);
    this.setMenu = this.setMenu.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
  }

  setMenu(reference) {
    this.menu = reference;
  }

  evaluateAction(actionKey, fallbackAllowed) {
    const {
      permissions = [],
      isRbacEnabled = false,
      educatorOnly,
    } = this.props;

    // In educator area: if user has educator area permission (fallbackAllowed is true), they can do everything
    // Skip all permission checks when in educator area
    if (educatorOnly && fallbackAllowed) {
      return { allowed: true };
    }

    // If RBAC is disabled (feature flag is false), ignore permissions and use fallback
    if (!isRbacEnabled) {
      return evaluateMaterialAction(actionKey, {
        permissions: [], // Pass empty array to force fallback usage
        fallback: fallbackAllowed,
      });
    }
    // If RBAC is enabled, check permissions strictly
    const hasResolvedPermissions =
      Array.isArray(permissions) && permissions.length > 0;
    const allowFallback = hasResolvedPermissions ? false : fallbackAllowed;
    return evaluateMaterialAction(actionKey, {
      permissions,
      fallback: allowFallback,
    });
  }

  async handleFileUpload(event) {
    // for some reason FileList does not have a map method
    const files = [...event.target.files];
    const fileContentPromises = files.map(readUploadedFileContent);
    const materials = await Promise.all(fileContentPromises);
    this.menu.handleClose(event);

    const { onImportMaterial, educatorOnly } = this.props;
    onImportMaterial({ materials, educatorOnly });
  }

  handleSelect({ item, preventClose }) {
    const {
      onCreateFolder,
      onExportMaterial,
      educatorOnly,
      onRestorePage,
      canCreateFolder = true,
      canImportMaterial = true,
      canExportMaterial = true,
    } = this.props;

    const createStatus = this.evaluateAction('CREATE_PAGE', canCreateFolder);
    const importStatus = this.evaluateAction(
      'IMPORT_MATERIAL',
      canImportMaterial
    );
    const exportStatus = this.evaluateAction(
      'EXPORT_MATERIAL',
      canExportMaterial
    );
    const restoreStatus = this.evaluateAction('RESTORE_PAGE', canCreateFolder);

    switch (item) {
      case 'create-folder':
        if (createStatus.allowed) {
          onCreateFolder(educatorOnly);
        }
        break;
      case 'import':
        if (importStatus.allowed) {
          if (preventClose) {
            preventClose();
          }
          document.getElementById('upload-button').click();
        }
        break;
      case 'export':
        if (exportStatus.allowed) {
          onExportMaterial(educatorOnly);
        }
        break;
      case 'restore':
        if (restoreStatus.allowed) {
          onRestorePage();
        }
        break;
      default:
    }
  }

  render() {
    const {
      onExportMaterial,
      onImportMaterial,
      onCreateFolder,
      onRestorePage,
      canCreateFolder = true,
      canImportMaterial = true,
      canExportMaterial = true,
      permissions,
      isRbacEnabled,
      isStudent = false,
      ...menuProps
    } = this.props;

    const createStatus = this.evaluateAction('CREATE_PAGE', canCreateFolder);
    const importStatus = this.evaluateAction(
      'IMPORT_MATERIAL',
      canImportMaterial
    );
    const exportStatus = this.evaluateAction(
      'EXPORT_MATERIAL',
      canExportMaterial
    );
    const restoreStatus = this.evaluateAction('RESTORE_PAGE', canCreateFolder);

    // For students: only show menu if they have at least one permission
    // For instructors: always show menu (items may be disabled)
    const hasAnyPermission =
      createStatus.allowed ||
      importStatus.allowed ||
      exportStatus.allowed ||
      (onRestorePage && restoreStatus.allowed);
    const shouldShowMenu = isStudent ? isRbacEnabled && hasAnyPermission : true;

    if (!shouldShowMenu) {
      return null;
    }

    return (
      <React.Fragment>
        <Menu
          ref={this.setMenu}
          ariaLabelledby="menu-materials"
          classes={{ menu: moreMenu }}
          {...menuProps}
          renderTarget={({ handleTargetClick, isOpen }) => (
            <Tooltip title={MATERIAL_IMPORT_EXPORT_TOOLTIP} position="top">
              <div>
                <IconButton
                  type="secondary"
                  size="xSmall"
                  icon={MoreVert}
                  label="more options"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTargetClick(e);
                  }}
                  buttonAttributes={{
                    'aria-expanded': isOpen,
                    'data-testid': 'materials-more-options',
                  }}
                  position="top"
                />
              </div>
            </Tooltip>
          )}
          onSelect={this.handleSelect}
          renderChildren={({ getItemProps, highlightedIndex }) => {
            const items = [];

            // For students: only show items they have permission for
            // For instructors: show all items (may be disabled)
            const pushItem = ({ key, label, itemKey, status, dataTestId }) => {
              // For students: skip items they don't have permission for
              if (isStudent && isRbacEnabled && !status.allowed) {
                return;
              }

              const listItem = (
                <ListItem
                  className={menuItem}
                  key={key}
                  primaryText={uppercaseFirst(label)}
                  highlighted={highlightedIndex === items.length}
                  attributes={{ 'data-testid': dataTestId }}
                  disabled={!status.allowed}
                  {...getItemProps({
                    item: status.allowed ? itemKey : null,
                    onClick: (e) => {
                      if (!status.allowed) {
                        e.stopPropagation();
                      }
                    },
                  })}
                />
              );
              items.push(
                status.message && !status.allowed ? (
                  <Tooltip title={status.message} key={`${key}-tooltip`}>
                    <div>{listItem}</div>
                  </Tooltip>
                ) : (
                  listItem
                )
              );
            };

            pushItem({
              key: MATERIAL_CREATE_FOLDER,
              label: MATERIAL_CREATE_FOLDER,
              itemKey: 'create-folder',
              status: createStatus,
              dataTestId: 'materials-create-folder',
            });

            const itemsBeforeSeparator = items.length;

            pushItem({
              key: IMPORT_MATERIAL,
              label: IMPORT_MATERIAL,
              itemKey: 'import',
              status: importStatus,
              dataTestId: 'materials-import',
            });

            pushItem({
              key: EXPORT_MATERIAL,
              label: EXPORT_MATERIAL,
              itemKey: 'export',
              status: exportStatus,
              dataTestId: 'materials-export',
            });

            if (onRestorePage) {
              pushItem({
                key: RESTORE_PAGE,
                label: RESTORE_PAGE,
                itemKey: 'restore',
                status: restoreStatus,
                dataTestId: 'materials-restore',
              });
            }

            // Only show separator if there are items before and after it
            const itemsAfterSeparator = items.length - itemsBeforeSeparator;
            if (itemsBeforeSeparator > 0 && itemsAfterSeparator > 0) {
              items.splice(
                itemsBeforeSeparator,
                0,
                <Separator
                  key="separator"
                  attributes={{ 'aria-hidden': true }}
                />
              );
            }

            return items;
          }}
        />
        <UploadButton
          aria-hidden="true"
          type="file"
          multiple
          id="upload-button"
          onChange={this.handleFileUpload}
        />
      </React.Fragment>
    );
  }
}

MaterialMenu.propTypes = {
  onCreateFolder: PropTypes.func.isRequired,
  onImportMaterial: PropTypes.func.isRequired,
  onExportMaterial: PropTypes.func.isRequired,
  educatorOnly: PropTypes.bool,
  onRestorePage: PropTypes.func.isRequired,
  canCreateFolder: PropTypes.bool,
  canImportMaterial: PropTypes.bool,
  canExportMaterial: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isRbacEnabled: PropTypes.bool,
  isStudent: PropTypes.bool,
};

MaterialMenu.defaultProps = {
  educatorOnly: false,
  canCreateFolder: true,
  canImportMaterial: true,
  canExportMaterial: true,
  permissions: [],
  isRbacEnabled: false,
  isStudent: false,
};

export default MaterialMenu;
